import { isEntityKycCustomer, requiresDocusignForKyc } from "../../../../shared/kycAuthentication";
import { ReactElement, useContext, useEffect, useState } from 'react'
import { CustomerSelectComponent } from '../../components/customer_select_component';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import { UseFormReturnType } from '@mantine/form';
import { Alert, Button, Checkbox, Divider, Grid, Group, Loader, Radio, SegmentedControl, Select, Space, Stack, Text, TextInput, Title } from '@mantine/core';
import { AddServiceRequest, BaseResult, CustomAutocomplete, CustomClaims, Customer, IndividualIDType, RMUser, SendServiceSMSRequest, Service, ServiceType, UpdateLink } from '../../types';
import { RmsSelectComponent } from '../../components/rms_select_component';
import { IconAlertCircle } from '@tabler/icons-react';
import { MessagesSent } from '../../components/send_sms_modal';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { useNavigate } from 'react-router-dom';
import { useDebouncedState } from '@mantine/hooks';
import CustomModal from '../../components/custom_modal';
import RequestKYCUpdate from '../requestKYCUpdate';
import { formatMobileWithDialCode } from "../../components/mobile_code_select";
import { civilValid, getCivilIDAge } from '../../utils';

const MINIMUM_SERVICE_SIGNING_AGE = 21;
type BaseServiceFormProps = {
    form: UseFormReturnType<AddServiceRequest>,
    serviceTypes: ServiceType[],
    successRedirectPath?: string
    preSubmitFormValues?: (formValues: AddServiceRequest) => AddServiceRequest
    setCustomerServices?: React.Dispatch<React.SetStateAction<Service[]>>
    children?: ReactElement,

}
function BaseServiceForm({ form, serviceTypes, children, preSubmitFormValues, successRedirectPath = "services" }: BaseServiceFormProps) {
    const db = useFirestore();
    const functions = useFunctions();

    const userContext = useContext(MyUserDataContext);
    const config = useContext(ConfigContext);

    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const navigate = useNavigate();
    const [customerData, setCustomerData] = useState<Customer[]>([]);
    const [customerID, setCustomerID] = useState<string | null>(null)
    const [customerObj, setCustomerObj] = useDebouncedState<Customer | undefined>(undefined, 200)
    const [customerType, setCustomerType] = useState<Customer["customerType"]>("individual");
    const [customerCivilIDInputError, setCustomerCivilIDInputError] = useState<string | undefined>();
    const [customerCivilID, setCustomerCivilID] = useState<string>()
    const [individualIdType, setIndividualIdType] = useState<IndividualIDType>("civilID");

    const [onBehalfOfChecked, setOnBehalfOfChecked] = useState(false);
    const [pending, setPending] = useState(false);
    const [showSendOrderButton, setShowSendOrderButton] = useState(true);
    const [isError, setIsError] = useState(false);
    const [SMSDocID, setSMSDocID] = useState<string>();
    const [createdService, setCreatedService] = useState<Service>();
    const [msg, setMsg] = useState<string | ReactElement | null>(null);
    const [toBeUpdatedCustomer, setToBeUpdatedCustomer] = useState<Customer | undefined>(undefined)
    const [addClientModal, setAddClientModal] = useState(false);

    const modalCustomerIdentifier = toBeUpdatedCustomer?.civilID || toBeUpdatedCustomer?.other_id || toBeUpdatedCustomer?.commercial_register;
    const handleDefaultSubmit = form.onSubmit((formValues) => {
        serviceHandleSubmit(formValues)
    });
    const handleContractSubmit = form.onSubmit((formValues) => {
        serviceHandleSubmit(formValues, { useContractService: true })
    }
    );
    const getCustomerByField = async (field: "civilID" | "other_id" | "commercial_register", value: string) => {
        const normalizedValue = value?.trim();
        if (!normalizedValue) {
            setToBeUpdatedCustomer(undefined);
            setAddClientModal(true);
            return false;
        }

        const customerSnapshot = await getDocs(query(collection(db, 'customers'), where(field, '==', normalizedValue), limit(1)));
        const customerData = customerSnapshot.docs[0]?.data() as Customer | undefined;
        console.log("customerData", customerData);
        if (customerData) {
            setCustomerData([customerData]);
            setCustomerObj(customerData);

            if (customerData.customerType === "individual" && (!customerData.gender || !customerData.nationality)) {
                setToBeUpdatedCustomer(customerData);
                form.setFieldValue("service.customerObj", customerData);
                setAddClientModal(true);
            } else if ((customerData.customerType === "company" || !!customerData.commercial_register) && (!customerData.nationality)) {
                setToBeUpdatedCustomer(customerData);
                form.setFieldValue("service.customerObj", customerData);
                setAddClientModal(true);
            } else {
                form.setFieldValue("service.customerObj", customerData);
                setToBeUpdatedCustomer(undefined);
                setAddClientModal(false);
            }
            setCustomerCivilIDInputError(undefined);
            return true;
        }

        setToBeUpdatedCustomer(undefined);
        setAddClientModal(true);
        return false;
    }
    const getOtherIDCustomerData = async (otherId: string) => {
        return getCustomerByField("other_id", otherId);
    };
    const getCustomerData = async (civilId: string) => {
        return getCustomerByField("civilID", civilId);
    };

    const getCompanyData = async (commercial_register_id: string) => {
        return getCustomerByField("commercial_register", commercial_register_id);
    };
    const getCustomerDataByDocumentID = async (customerDocID: string) => {
        const customerSnapshot = await getDoc(doc(db, 'customers', customerDocID));
        const customerData = customerSnapshot.data() as Customer | undefined;
        if (customerData) {
            setCustomerData([customerData]);
            setCustomerObj(customerData);
            if (customerData.customerType === "individual" && (!customerData.gender || !customerData.nationality)) {
                setToBeUpdatedCustomer(customerData);
                form.setFieldValue("service.customerObj", customerData);
                setAddClientModal(true);
            } else {
                form.setFieldValue("service.customerObj", customerData);
                setToBeUpdatedCustomer(undefined);
                setAddClientModal(false);
            }
            return true;
        }
        else {
            setToBeUpdatedCustomer(undefined);
            setAddClientModal(true);
            return false;
        }
    }


    const serviceHandleSubmit = async (
        formValues: AddServiceRequest,
        options?: { useContractService?: boolean }
    ) => {
        const shouldUseContractService = options?.useContractService === true;

        setMsg(null);
        setIsError(false);


        // Use FormData to retrieve the form values
        console.log("submitting", form.values.action);

        // const values: (Order & UpdateLink) = formValues as (Order & UpdateLink)
        setMsg(null);
        setPending(true);

        let formValuesToBeSent = { ...formValues };  //I'm making a copy because I might modify things here.  I don't want the form to reflect those changes.

        try {
            if (preSubmitFormValues) {
                formValuesToBeSent = preSubmitFormValues(formValues);
            }
            const getAzureADToken = userContext.getAzureADToken;
            const token = getAzureADToken ? await getAzureADToken(userContext) : undefined;
            // console.log("Token", token);

            if (userContext.user?.providerId === "microsoft.com" && !token) {
                setIsError(true);
                console.log("No Microsoft Token");
                setMsg("No Microsoft Token.  Try refreshing.");
                return;
            }
            else if (token) {
                formValuesToBeSent.token = token;
            }

            //generate a doc ID
            const docID = doc(collection(db, "sms")).id;
            setSMSDocID(docID);
            formValuesToBeSent.updateLink.smsDocID = docID;

            formValuesToBeSent.id = customerObj?.id;

            const addServiceCallable = httpsCallable<AddServiceRequest, BaseResult>(functions, "addService");
            const addContractService = httpsCallable<AddServiceRequest, BaseResult>(functions, "addContractService");


            const res = shouldUseContractService
                ? await addContractService(formValuesToBeSent)
                : await addServiceCallable(formValuesToBeSent);
            console.log(res);

            const returnedServiceData = res.data.data;

            // e.target.reset();
            //the href is absolutely stupid.  I'm reutilizing the sendBatchSMS from firebase functions, so yeah.
            setMsg(
                <Stack>

                    <Text>{form.values.service.serviceType} service created successfully {" "}

                        <Text span >
                            <a href={`/${successRedirectPath}/${res.data.data?.id}`}>

                                view
                            </a>
                        </Text>
                    </Text>

                </Stack>
            );
            setIsError(false);
            if (returnedServiceData) {
                setCreatedService(returnedServiceData as Service);
                // navigate("/services", {
                //   state: {
                //     refreshServicesIn: 1000,
                //   }
                // });
            }

            setShowSendOrderButton(false);

        } catch (error: unknown) {
            setIsError(true);
            console.log(error);
            setMsg(error instanceof Error ? error.message : "Unexpected error");
        }

        setPending(false);
    };
    useEffect(() => {
        const ID = customerID;


        if (ID) {
            console.log("We got ID", ID);

            //find customer from ID
            const customer = customerData?.find((customer: Customer) => customer.id === ID);

            console.log("We got customer", customer);

            if (customer) {
                setCustomerObj(customer);
                if (customer.customerType === "individual" && (!customer.gender || !customer.nationality)) {
                    setToBeUpdatedCustomer(customer);
                    setAddClientModal(true);
                } else {
                    setToBeUpdatedCustomer(undefined);
                }
            } else {
                setToBeUpdatedCustomer(undefined);
            }
            form.setFieldValue("service.customerObj", customer);
        }
        else {
            setCustomerObj(undefined);
            setToBeUpdatedCustomer(undefined);
            form.setFieldValue("service.customerObj", undefined);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerID]);


    useEffect(() => {
        const identifier = customerCivilID?.trim();
        if (identifier) {
            if (customerType === "individual" && individualIdType === "civilID") {
                if (identifier.length === 12) {
                    if (civilValid(identifier)) {
                        getCustomerData(identifier)
                        setCustomerCivilIDInputError(undefined);
                    } else {
                        setCustomerObj(undefined);
                        form.setFieldValue("service.customerObj", undefined);
                        setCustomerCivilIDInputError("Invalid Civil ID");
                    }
                } else {
                    setCustomerObj(undefined);
                    form.setFieldValue("service.customerObj", undefined);
                    setCustomerCivilIDInputError(undefined);
                }
            } else {
                // For company and other ID lookups we fetch only when user clicks "Get".
                setCustomerObj(undefined);
                form.setFieldValue("service.customerObj", undefined);
                setToBeUpdatedCustomer(undefined);
                setCustomerCivilIDInputError(undefined);
            }
        }
        else {
            setCustomerObj(undefined);
            form.setFieldValue("service.customerObj", undefined);
            setCustomerCivilIDInputError(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerCivilID, customerType, individualIdType])

    const customerCivilIDForAgeCheck =
        form.values.service.customerObj?.civilID?.trim() ||
        (customerType === "individual" && individualIdType === "civilID"
            ? customerCivilID?.trim()
            : undefined);
    const customerAge = customerCivilIDForAgeCheck ? getCivilIDAge(customerCivilIDForAgeCheck) : null;
    const isCustomerUnderMinimumSigningAge = customerAge !== null && customerAge < MINIMUM_SERVICE_SIGNING_AGE;
    // Entities authenticate through their signatory; only other-ID-only individuals require DocuSign.
    const isOtherIdOnlyCustomer = requiresDocusignForKyc(form.values.service.customerObj ?? {});

    useEffect(() => {
        if (!form.values.signingMethod) {
            form.setFieldValue("signingMethod", "paci");
        }
        if (isCustomerUnderMinimumSigningAge) {
            form.setFieldValue("signingMethod", "paci");
        } else if (isOtherIdOnlyCustomer && form.values.signingMethod !== "docusign") {
            form.setFieldValue("signingMethod", "docusign");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCustomerUnderMinimumSigningAge, isOtherIdOnlyCustomer, form.values.signingMethod]);



    return (
        <>

            <form onSubmit={handleDefaultSubmit}>
                <Grid>
                    <Grid.Col span={{ xs: 12 }}>

                        <Text
                            variant="gradient"
                            gradient={{
                                from: form.errors?.civilID ? "#fa5252" : "indigo",
                                to: form.errors?.civilID ? "#FF6B6B" : "cyan",
                                deg: 45,
                            }}
                            style={{ fontFamily: 'Greycliff CF, sans-serif' }}
                            //   ta="center"
                            fz="xl"
                            fw={700}
                        >
                            Customer Information
                        </Text>
                    </Grid.Col>

                    <Grid.Col span={{ xs: 12, lg: 6 }}>


                        {
                            !userClaims["kfh_rm"] ?
                                <Grid.Col span={{ xs: 12, lg: 12 }}>
                                    <CustomerSelectComponent
                                        setCustomerData={setCustomerData}
                                        customerData={customerData as (Customer & CustomAutocomplete)[]}
                                        defaultValue={form.values.service.customerObj?.value}
                                        // ref={civilIDRef}

                                        textProps={{
                                            label: "Civil ID, Other ID or Commercial Registry",
                                            placeholder: "Enter Civil ID, Other ID or Commercial Registry",
                                            disabled: pending
                                        }}
                                        handleSubmit={(customerIDValue) => {
                                            console.log("We set ID to", customerIDValue);
                                            setCustomerID(customerIDValue as string)

                                        }}

                                    />
                                </Grid.Col>
                                :
                                <>
                                    <Grid.Col span={{ xs: 12, lg: 6 }}>

                                        <Radio.Group
                                            label="Customer Type"
                                            required
                                            value={customerType}
                                            onChange={(value) => {
                                                setCustomerType(value as Customer["customerType"]);
                                                setCustomerCivilID(undefined);
                                                setCustomerID(null);
                                                setCustomerCivilIDInputError(undefined);
                                                setToBeUpdatedCustomer(undefined);
                                                form.setFieldValue("service.customerObj", undefined);
                                            }}
                                        >
                                            <Group>
                                                <Radio value={"individual"} label="Individual" />
                                                <Radio value={"company"} label="Company" />

                                            </Group>
                                        </Radio.Group>
                                        {customerType === "individual" && <Radio.Group
                                            mt="md"
                                            label="Individual ID Type"
                                            required
                                            value={individualIdType}
                                            onChange={(value) => {
                                                setIndividualIdType(value as IndividualIDType);
                                                setCustomerCivilID(undefined);
                                                setCustomerID(null);
                                                setCustomerCivilIDInputError(undefined);
                                                form.setFieldValue("service.customerObj", undefined);
                                            }}
                                        >
                                            <Group>
                                                <Radio value={"civilID"} label="Kuwait Civil ID" />
                                                <Radio value={"other_id"} label="Other ID" />
                                            </Group>
                                        </Radio.Group>}

                                    </Grid.Col>
                                    <Grid.Col span={{ xs: 12, lg: 6 }}>
                                        <Text>
                                            {
                                                customerType === "company"
                                                    ? "Commercial Registry"
                                                    : individualIdType === "civilID"
                                                        ? "Civil ID"
                                                        : "Other ID (Passport / National ID)"
                                            }

                                        </Text>
                                        <Group>
                                            <TextInput
                                                style={{ width: (customerType === "company" || (customerType === "individual" && individualIdType === "other_id")) ? "80%" : "100%" }}
                                                disabled={pending}

                                                value={form.values.service.customerObj?.value || customerCivilID}
                                                onChange={(event) => {
                                                    setCustomerCivilID(event.currentTarget.value);

                                                }}
                                                error={customerCivilIDInputError}
                                                onKeyDown={(e) => {
                                                    // console.log("ON KEY DOWN!!!", e.key);
                                                    //was it a backspace?
                                                    if (e.key === "Backspace") {
                                                        console.log("backspace");
                                                        setCustomerID(null);

                                                    }
                                                }}

                                            />
                                            {(customerType === "company" || (customerType === "individual" && individualIdType === "other_id")) && <Button
                                                disabled={!customerCivilID}
                                                onClick={() => {
                                                    if (customerCivilID) {
                                                        if (customerType === "company") {
                                                            getCompanyData(customerCivilID);
                                                        } else {
                                                            getOtherIDCustomerData(customerCivilID);
                                                        }
                                                    }
                                                }}>
                                                Get
                                            </Button>}
                                        </Group>

                                    </Grid.Col>
                                </>
                        }

                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, lg: 6 }}>

                        <TextInput
                            // withAsterisk
                            label="Email"
                            // placeholder="their@email.com"
                            disabled
                            value={form.values?.service.customerObj?.email || ""}

                        />
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, lg: 6 }}>

                        <TextInput
                            // withAsterisk
                            label="Name (English)"
                            // placeholder="Joe"
                            disabled
                            // {...form.getInputProps("customerObj.name_en")}
                            value={form.values?.service.customerObj?.name_en || ""}

                        />
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, lg: 6 }}>

                        <TextInput
                            // withAsterisk
                            label="Name (Arabic)"
                            // placeholder="جو"
                            disabled
                            value={form.values?.service.customerObj?.name_ar || ""}

                        // {...form.getInputProps("customerObj.name_ar")}
                        />
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, lg: 6 }}>
                        <TextInput
                            // withAsterisk
                            label="Mobile"
                            // placeholder="xxxxxxxx"
                            disabled
                            value={formatMobileWithDialCode(form.values?.service.customerObj?.mobile, form.values?.service.customerObj?.mobile_code)}

                        // {...form.getInputProps("customerObj.mobile")}
                        />
                    </Grid.Col >

                    {isEntityKycCustomer(form.values.service.customerObj ?? {})
                        ? <Grid.Col span={{ xs: 12, lg: 6 }}>
                            <TextInput
                                label="KYC Link Whatsapp Number"
                                placeholder="Enter Mobile number"
                                required
                                {...form.getInputProps("service.kycLinkWhatsappNumber")}
                            />
                        </Grid.Col>
                        : undefined
                    }
                    <Divider my={30} style={{ width: "100%" }} />



                </Grid>

                <Grid>
                    <Grid.Col span={{ xs: 12, lg: 6 }}>
                        <Stack gap={6}>
                            <Text size="sm" fw={500}>Contract signing method</Text>
                            <SegmentedControl
                                fullWidth
                                value={isOtherIdOnlyCustomer ? "docusign" : (form.values.signingMethod ?? "paci")}
                                disabled={isCustomerUnderMinimumSigningAge || isOtherIdOnlyCustomer}
                                data={[
                                    { value: "paci", label: "PACI / Kuwait Mobile ID" },
                                    { value: "docusign", label: "DocuSign email" },
                                ]}
                                onChange={(value) =>
                                    form.setFieldValue(
                                        "signingMethod",
                                        value as AddServiceRequest["signingMethod"],
                                    )
                                }
                            />
                            {isCustomerUnderMinimumSigningAge && (
                                <Text size="xs" c="dimmed">
                                    DocuSign is disabled for minor clients until guardian signer routing is implemented.
                                </Text>
                            )}
                            {isOtherIdOnlyCustomer && (
                                <Text size="xs" c="dimmed">
                                    Client has no Kuwait Civil ID (Other ID only), so PACI is not available: the contract is signed through DocuSign, sent to {form.values.service.customerObj?.email || "the client's e-mail (missing! update the client first)"}.
                                </Text>
                            )}
                        </Stack>
                    </Grid.Col>
                </Grid>


                {/*  <Divider my={30} style={{ width: "100%" }} /> */}
                <Grid>
                    <Grid.Col span={{ xs: 24, lg: 24 }}>

                        <Text
                            variant="gradient"
                            gradient={{ from: "indigo", to: "cyan", deg: 45 }}
                            style={{ fontFamily: 'Greycliff CF, sans-serif' }}
                            //   ta="center"
                            fz="xl"
                            fw={700}
                        >
                            Service Type
                        </Text>
                    </Grid.Col>
                    <Grid.Col span={{ xs: 3 }}>

                        <Select
                            label="Type"
                            // name="type"
                            data={config?.serviceSettings?.availableServices?.filter((serviceObj) => serviceTypes?.length ? serviceTypes.includes(serviceObj.value) : true) || []}
                            {...form.getInputProps("service.serviceType")}
                            onChange={(value: string | null) => {
                                if (value) {
                                    form.setFieldValue("service.serviceType", value as ServiceType)
                                    form.setFieldValue("service.type", value as ServiceType)

                                }
                            }}
                        />
                    </Grid.Col>


                </Grid>




                {children && <>
                    <Divider my={30} style={{ width: "100%" }} />

                    {children}
                </>}

                <Divider my={30} style={{ width: "100%" }} />

                <Grid>
                    <Grid.Col span={{ xs: 24, lg: 24 }}>

                        <Text
                            variant="gradient"
                            gradient={{ from: "indigo", to: "cyan", deg: 45 }}
                            style={{ fontFamily: 'Greycliff CF, sans-serif' }}
                            //   ta="center"
                            fz="xl"
                            fw={700}
                        >
                            RM Information
                        </Text>
                    </Grid.Col>
                    <Grid.Col span={{ xs: 12, }}>

                        <Grid >

                            <Grid.Col span={{ xs: 4 }} >

                                <Text>
                                    {userContext.user?.displayName}
                                </Text>
                            </Grid.Col>

                            <Grid.Col span={{ xs: 6 }} >

                                <Checkbox
                                    checked={onBehalfOfChecked}

                                    disabled={pending}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            form.setFieldValue("onBehalfOf", undefined);

                                        }
                                        setOnBehalfOfChecked(e.target.checked)
                                    }}

                                    label="On behalf of"
                                />

                            </Grid.Col>
                            <Grid.Col span={{ lg: 6 }}>
                                <RmsSelectComponent


                                    defaultValue={form.values.service.rm?.rm_id}

                                    textProps={{
                                        label: "On Behalf of RM:",
                                        placeholder: "search RM by name or email",
                                        disabled: pending || !onBehalfOfChecked

                                    }}
                                    handleSubmit={(selectedRM: RMUser) => {
                                        form.setFieldValue("onBehalfOf", selectedRM);
                                    }}

                                />



                            </Grid.Col>
                        </Grid>


                    </Grid.Col >



                    <Grid.Col span={{ xs: 12, }}>
                        <Space h="xl" />
                        {msg != null && ( //love my check if it's an error or not.  Please don't do this, it's just a proof of concept page.
                            <Alert
                                icon={<IconAlertCircle size={16} />}
                                color={isError ? "red" : "green"}
                                title={isError ? "Failure" : "Success"}
                            >
                                {msg}
                            </Alert>
                        )}
                        {form.errors != null && Object.keys(form.errors).length > 0 && (
                            <>
                                <Alert
                                    icon={<IconAlertCircle size={16} />}
                                    color="red"
                                    title="Check form"
                                >
                                    {Object.values(form.errors).join(", ")}
                                </Alert>
                                {/* <Text>{JSON.stringify(form.values)}</Text> */}
                            </>
                        )}
                    </Grid.Col>
                    <Space h="xl" />
                    {SMSDocID &&
                        <Grid.Col span={12} >


                            <MessagesSent docID={SMSDocID} handleFailedMessages={() => {

                                const handleServiceSMS = async () => {

                                    const sendServiceSMS = httpsCallable<SendServiceSMSRequest, BaseResult>(functions, "sendServiceSMS");


                                    //generate a doc ID
                                    const docID = doc(collection(db, "sms")).id;
                                    setSMSDocID(docID);

                                    //set type to submit because we want the user to create an entirely new KYC
                                    if (!createdService) {
                                        return;
                                    }
                                    const getAzureADToken = userContext.getAzureADToken;
                                    const smsToken = getAzureADToken ? await getAzureADToken(userContext) : undefined;
                                    await sendServiceSMS({
                                        service: createdService,
                                        smsDocID: docID,
                                        token: smsToken
                                    });
                                }
                                handleServiceSMS();
                            }} />




                        </Grid.Col>
                    }

                    {createdService && <Button fullWidth mt={5}
                        onClick={() => navigate(`/${successRedirectPath}`, {
                            state: {
                                refreshServicesIn: 1000,
                            }
                        })}
                        variant='outline'
                        color={"darkgreen"}
                    >
                        Back to services
                    </Button>}
                    {showSendOrderButton && <Grid.Col span={{ xs: 12 }}>

                        <Group justify="center" mt="md">






                            {userClaims && (userClaims.kfh_rm || userClaims.kfh_oper) &&
                                < Button
                                    type="button"
                                    variant="outline"
                                    color="orange"
                                    loading={pending}
                                    onClick={() => {
                                        form.setFieldValue("action", "send_service_sms");
                                        handleContractSubmit();
                                    }}
                                >
                                    Send via Contract Service
                                </Button>}



                            {(userClaims.admin || userClaims.cr) && (
                                <>
                                    {/* <Button type="submit" loading={pending}
                                    // disabled={isSubmitted}
                                    >
                                        Send Service Message
                                    </Button> */}
                                    <Button
                                        type="button"
                                        variant="outline"
                                        color="orange"
                                        loading={pending}
                                        onClick={() => {
                                            form.setFieldValue("action", "send_service_sms");
                                            handleContractSubmit();
                                        }}
                                    >
                                        Send via Contract Service
                                    </Button>
                                </>
                            )}



                        </Group>

                    </Grid.Col>}

                </Grid >
            </form >
            {modalCustomerIdentifier && <CustomModal
                title={<Title>{toBeUpdatedCustomer === undefined ? "Add Client" : "Update Client"}</Title>}
                opened={addClientModal}
                onClose={async () => {
                    setAddClientModal(false);
                }}
                centered
            >
                <RequestKYCUpdate civilID={modalCustomerIdentifier} customer={toBeUpdatedCustomer} isUpdate={toBeUpdatedCustomer !== undefined}

                    onClose={() => {
                        if (toBeUpdatedCustomer?.id) {
                            getCustomerDataByDocumentID(toBeUpdatedCustomer.id)
                        }

                    }}
                    setModal={setAddClientModal} />
            </CustomModal>
            }
        </>
    )
}

export default BaseServiceForm
